"use client";

import { useEffect } from "react";
import { useWebSocket } from "@/shared/contexts/WebSocketContext";
import type {
  WardrobeItemPayload,
  WardrobeItemDeletedPayload,
  BatchProgressPayload,
  ProcessingStepPayload,
  UploadItemReadyPayload,
  UploadItemFailedPayload,
  UploadJobCompletePayload,
  SetProcessingStepPayload,
} from "@/shared/lib/websocket";

/**
 * Hook to subscribe to wardrobe item created events
 * Automatically unsubscribes on unmount or dependency change
 */
export function useWardrobeItemCreated(
  callback: (item: WardrobeItemPayload) => void,
  deps: React.DependencyList = []
) {
  const { onWardrobeItemCreated, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = onWardrobeItemCreated(callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, onWardrobeItemCreated, ...deps]);
}

/**
 * Hook to subscribe to wardrobe item deleted events
 * Automatically unsubscribes on unmount or dependency change
 */
export function useWardrobeItemDeleted(
  callback: (payload: WardrobeItemDeletedPayload) => void,
  deps: React.DependencyList = []
) {
  const { onWardrobeItemDeleted, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = onWardrobeItemDeleted(callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, onWardrobeItemDeleted, ...deps]);
}

/**
 * Hook to subscribe to batch progress events
 * Automatically unsubscribes on unmount or dependency change
 */
export function useBatchProgress(
  callback: (payload: BatchProgressPayload) => void,
  deps: React.DependencyList = []
) {
  const { onBatchProgress, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = onBatchProgress(callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, onBatchProgress, ...deps]);
}

/**
 * Hook to subscribe to processing step events
 * Automatically unsubscribes on unmount or dependency change
 */
export function useProcessingStep(
  callback: (payload: ProcessingStepPayload) => void,
  deps: React.DependencyList = []
) {
  const { onProcessingStep, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = onProcessingStep(callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, onProcessingStep, ...deps]);
}

/**
 * Hook to subscribe to upload item ready events
 */
export function useUploadItemReady(
  callback: (payload: UploadItemReadyPayload) => void,
  deps: React.DependencyList = []
) {
  const { onUploadItemReady, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = onUploadItemReady(callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, onUploadItemReady, ...deps]);
}

/**
 * Hook to subscribe to upload item failed events
 */
export function useUploadItemFailed(
  callback: (payload: UploadItemFailedPayload) => void,
  deps: React.DependencyList = []
) {
  const { onUploadItemFailed, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = onUploadItemFailed(callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, onUploadItemFailed, ...deps]);
}

/**
 * Hook to subscribe to upload job complete events
 */
export function useUploadJobComplete(
  callback: (payload: UploadJobCompletePayload) => void,
  deps: React.DependencyList = []
) {
  const { onUploadJobComplete, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = onUploadJobComplete(callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, onUploadJobComplete, ...deps]);
}

/**
 * Hook to subscribe to set processing step events
 */
export function useSetProcessingStep(
  callback: (payload: SetProcessingStepPayload) => void,
  deps: React.DependencyList = []
) {
  const { onSetProcessingStep, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = onSetProcessingStep(callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, onSetProcessingStep, ...deps]);
}

/**
 * Hook to get the WebSocket connection status
 */
export function useSocketStatus() {
  const { isConnected, socket } = useWebSocket();
  return { isConnected, socket };
}
